import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ShieldCheck, CreditCard, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

export default function Checkout() {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [SquareSDK, setSquareSDK] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get(`/submissions/${submissionId}`),
      api.get("/payments/config"),
    ]).then(([s, c]) => {
      setSub(s.data);
      setConfig(c.data);
    }).catch(() => toast.error("Failed to load checkout"))
      .finally(() => setLoading(false));
  }, [submissionId]);

  useEffect(() => {
    if (config?.configured) {
      import("react-square-web-payments-sdk").then((mod) => setSquareSDK(mod));
    }
  }, [config]);

  const handleMockPay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/charge", { submission_id: submissionId, nonce: "mock_nonce" });
      toast.success(`Payment confirmed${data.mocked ? " (Sandbox)" : ""}.`);
      navigate("/submissions");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payment failed");
    } finally {
      setPaying(false);
    }
  };

  const handleSquareTokenized = async (token) => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/charge", { submission_id: submissionId, nonce: token.token });
      toast.success("Payment successful!");
      navigate("/submissions");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payment failed");
    } finally {
      setPaying(false);
    }
  };

  if (loading) return (
    <div className="App"><Header /><div className="rgu-container py-12 text-slate-500">Loading…</div><Footer /></div>
  );
  if (!sub) return null;

  const PaymentForm = SquareSDK?.PaymentForm;
  const CreditCardEl = SquareSDK?.CreditCard;

  return (
    <div className="App">
      <Header />
      <div className="rgu-container py-12 grid grid-cols-1 lg:grid-cols-12 gap-10" data-testid="checkout-page">
        <div className="lg:col-span-7">
          <div className="rgu-eyebrow text-slate-500 mb-3">Checkout · Payment</div>
          <h1 className="font-display text-3xl md:text-4xl text-[#0A192F]">Complete Your Order</h1>
          <p className="mt-3 text-slate-600">Secure payment processed by Square.</p>

          <div className="mt-8 border border-slate-200 p-6 bg-white">
            <div className="flex items-center gap-2 mb-4">
              <Lock className="w-4 h-4 text-[#0A192F]" />
              <div className="font-display text-base text-[#0A192F]">Card Details</div>
            </div>

            {config?.configured && PaymentForm ? (
              <PaymentForm
                applicationId={config.application_id}
                locationId={config.location_id}
                cardTokenizeResponseReceived={async (token) => {
                  if (token.status === "OK") await handleSquareTokenized(token);
                  else toast.error("Card tokenization failed");
                }}
              >
                <CreditCardEl />
              </PaymentForm>
            ) : (
              <div className="space-y-4">
                <div className="border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  <strong>SANDBOX MODE:</strong> Square keys not configured. This will simulate a successful payment to demo the full flow. Add Square credentials in <code>backend/.env</code> to enable real card processing.
                </div>
                <button
                  onClick={handleMockPay}
                  disabled={paying}
                  className="rgu-btn-primary w-full justify-center text-xs uppercase tracking-widest"
                  data-testid="mock-pay-btn"
                >
                  <CreditCard className="w-4 h-4" /> {paying ? "Processing…" : `Pay $${sub.quote.total.toFixed(2)} (Sandbox)`}
                </button>
              </div>
            )}
          </div>

          <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">
            <ShieldCheck className="w-4 h-4" /> PCI-compliant card tokenization. Card data is never stored on our servers.
          </div>
        </div>

        <aside className="lg:col-span-5">
          <div className="border border-slate-200 p-6 bg-slate-50 sticky top-28">
            <div className="rgu-eyebrow text-slate-500 mb-3">Order #{sub.id.slice(0, 8).toUpperCase()}</div>
            <div className="font-display text-xl text-[#0A192F]">
              {sub.is_follow_up ? "Letter of Opinion" : (sub.auth_type === "photo_match" ? "Photo Match Authentication" : "Traditional Authentication")}
            </div>
            <div className="mt-6 font-mono-rgu text-sm space-y-2">
              {sub.quote.breakdown.map((b, i) => (
                <div key={i} className="flex justify-between">
                  <span className="text-slate-600 truncate pr-2">{b.item_title}</span>
                  <span>${b.price.toFixed(2)}</span>
                </div>
              ))}
              <div className="border-t border-slate-200 pt-2 flex justify-between text-slate-600">
                <span>Subtotal</span><span>${sub.quote.subtotal.toFixed(2)}</span>
              </div>
              {sub.quote.discount_amount > 0 && (
                <div className="flex justify-between text-green-700">
                  <span>Bulk Discount</span><span>− ${sub.quote.discount_amount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-slate-600">
                <span>Shipping</span><span>${sub.quote.shipping_cost.toFixed(2)}</span>
              </div>
              <div className="border-t border-slate-300 pt-3 flex justify-between text-base text-[#0A192F]">
                <span className="font-display">Total</span>
                <span data-testid="checkout-total">${sub.quote.total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </aside>
      </div>
      <Footer />
    </div>
  );
}
